import moment from 'moment';
import * as api from '../api';
import * as utils from '../utils';
import * as i18next from 'i18next';

export class ConnectionModel {
    public duration: moment.Duration;
    public anotherAirport: boolean;
    public anotherTerminal: boolean;

    constructor(duration: moment.Duration, anotherAirport: boolean, anotherTerminal: boolean) {
        this.duration = duration;
        this.anotherAirport = anotherAirport;
        this.anotherTerminal = anotherTerminal;
    }
}

export class SegmentStopModel {
    public aircraft: api.Aircraft;
    public arrivalDate: moment.Moment;
    public departureDate: moment.Moment;
    public destination: api.Destination;
    public duration: moment.Duration;

    constructor(s: api.SegmentStop, d: api.FlightDictionaries) {
        this.destination = d.destinations[s.destinationUID];
        this.arrivalDate = moment.utc(s.arrivalDateLocal);
        this.departureDate = moment.utc(s.departureDateLocal);
        this.duration = moment.duration(moment.utc(s.arrivalDate).diff(moment.utc(s.departureDate)));
        this.aircraft = d.aircrafts[s.aircraftUID];
    }
}

export class SegmentModel {
    public aircraft: string;
    public airline: api.Airline; // Marketing airline
    public arrivalDate: moment.Moment;
    public arrivalDateUtc: moment.Moment;
    public arrivalDestination: api.Destination;
    public arrivalTerminal: string;
    public baggage: api.FlightBaggage;
    public baggageCabin: api.FlightBaggage;
    public connection: ConnectionModel;
    public departureDate: moment.Moment;
    public departureDateUtc: moment.Moment;
    public departureDestination: api.Destination;
    public departureTerminal: string;
    public duration: moment.Duration;
    public exchangeRules: api.FlightModifyRules;
    public segmentFlags: api.SegmentFlags[];
    public flightCode: string;
    public isNextDayArrival: boolean;
    public nextSegmentStop: api.FlightStopKind;
    public operatingAirline: api.Airline; // Validating airline
    public refundRules: api.FlightModifyRules;
    public stops: SegmentStopModel[]; // Technical stops during flight (refueling)
    public transport: api.SegmentTransport;

    constructor(s: api.Segment, d: api.FlightDictionaries) {
        this.aircraft = d.aircrafts[s.aircraftUID].displayName;
        this.airline = d.airlines[s.airlineUID];
        this.arrivalDate = moment.utc(s.arrivalDateLocal);
        this.arrivalDestination = d.destinations[s.arrivalDestinationUID];
        this.arrivalTerminal = s.arrivalTerminal;
        this.baggage = s.baggage;
        this.baggageCabin = s.baggageCabin;
        this.departureDate = moment.utc(s.departureDateLocal);
        this.departureDestination = d.destinations[s.departureDestinationUID];
        this.departureTerminal = s.departureTerminal;
        this.duration = moment.duration(moment(s.arrivalDate).diff(moment(s.departureDate)));
        this.exchangeRules = s.exchangeRules;
        this.segmentFlags = s.segmentFlags;
        this.flightCode = s.flightCode;
        this.isNextDayArrival = this.departureDate.date() !== this.arrivalDate.date();
        this.nextSegmentStop = s.nextSegmentStop === null
            ? api.FlightStopKind.Standard
            : s.nextSegmentStop;
        this.operatingAirline = d.airlines[s.operatingAirlineUID];
        this.refundRules = s.refundRules;
        if (s.stops && s.stops.length > 0) {
            this.stops = s.stops.map(segmentStop => new SegmentStopModel(segmentStop, d));
        }
        this.transport = s.transport;
    }

    public get hasNoBaggage(): boolean {
        return this.baggage
            && this.baggage.pieceWeight == 0
            && this.baggage.pieces == 0;
    }

    public get transportIconName() {
        switch (this.transport) {
            case 'Air': {
                return 'plane';
                break;
            }
            case 'Bus': {
                return 'bus';
                break;
            }
            case 'Train': {
                return 'train';
                break;
            }
            default:{
                return 'plane';
                break;
            }
        }
    }
}

export class LegModel {
    public arrivalDate: moment.Moment;
    public arrivalDestination: api.Destination;

    public get baggage(): api.FlightBaggage {
        return utils.first(this.segments).baggage;
    }

    public get baggageCabin(): api.FlightBaggage {
        return utils.first(this.segments).baggageCabin;
    }

    public baggageBySegments: boolean;
    public bookingClass: string;
    public departureDate: moment.Moment;
    public departureDestination: api.Destination;
    public duration: moment.Duration;
    public durationMinutes: number;
    public flightKind: api.FlightKind;
    public groupingId: string;
    public isNextDayArrival: boolean;
    public segments: SegmentModel[];
    public stopCount: number;
    public maintenanceStopCount: number;

    constructor(l: api.Leg, d: api.FlightDictionaries) {
        this.segments = l.segments.map(segment => new SegmentModel(segment, d));

        this.groupingId = l.groupingId;
        this.baggageBySegments = l.baggageBySegments;
        this.bookingClass = l.bookingClass;
        this.departureDate = utils.first(this.segments).departureDate;
        this.departureDestination = utils.first(this.segments).departureDestination;
        this.arrivalDate = utils.last(this.segments).arrivalDate;
        this.arrivalDestination = utils.last(this.segments).arrivalDestination;
        this.stopCount = this.segments.length - 1;
        this.isNextDayArrival = this.departureDate.date() !== this.arrivalDate.date();
        // use UTC dates from source api.Segment
        this.duration = moment.duration(moment(utils.last(l.segments).arrivalDate).diff(utils.first(l.segments).departureDate));
        // use UTC dates from source api.Segment
        this.durationMinutes = moment(utils.last(l.segments).arrivalDateUtc).diff(utils.first(l.segments).departureDateUtc, 'minute');
        this.flightKind = l.flightKind;

        // Build connections.
        for (let i = 0; i < this.segments.length - 1; i++) {
            const curSegment = this.segments[i];
            const nextSegment = this.segments[i + 1];
            // Use source api.Segment for UTC dates.
            const cd = moment.duration(moment(l.segments[i + 1].departureDate).diff(moment(l.segments[i].arrivalDate)));
            curSegment.connection = new ConnectionModel(
                cd,
                nextSegment.departureDestination.iataCode !== curSegment.arrivalDestination.iataCode,
                nextSegment.departureTerminal !== curSegment.arrivalTerminal
            );
        }

        // Count maintenance stops
        this.maintenanceStopCount = 0;
        this.segments.forEach(segment => {
            if (segment.stops && segment.stops.length > 0) {
                this.maintenanceStopCount += segment.stops.length;
            }
        });
    }

    get is2OWorIncut(): boolean {
        return !!this.segments.find(segment => segment.nextSegmentStop == api.FlightStopKind.ByPassenger);
    }

    get segmentsSplittedByNextSegmentStop2OWorIncut() {
        const result: SegmentModel[][] = [];
        let memo: SegmentModel[] = [];

        this.segments.forEach((segment, segmentIndex) => {
            memo = memo.concat(segment);
            if (segment.nextSegmentStop == api.FlightStopKind.ByPassenger
                || segmentIndex == this.segments.length - 1) {
                result.push((<SegmentModel[]>[]).concat(memo));
                memo = [];
            }
        });

        return result;
    }

    get transportTypes() {
        const legTransportTypes: api.SegmentTransport[] = [];

        this.segments.forEach((segment) => {
            if (!legTransportTypes.includes(segment.transport)) {
                legTransportTypes.push(segment.transport);
            }
        });

        return legTransportTypes;
    }

    get transportIconNames() {
        const legTransportIconNames: string[] = [];

        this.segments.forEach((segment) => {
            if (!legTransportIconNames.includes(segment.transportIconName)) {
                legTransportIconNames.push(segment.transportIconName);
            }
        });

        return legTransportIconNames;
    }
}

export class FlightModel {
    adapter: string;
    allFlightAirlinesCode: string[];
    airlines: api.Airline[];
    area: string;
    adapterData: string;
    basePrice = 0;
    billingPrice = 0;
    billingPricePerMonth = 0;
    bookingClass: string;
    bookingChecked: boolean;
    bookingPossible: boolean;
    brand: api.FlightBrand;
    currencyCode?: string;
    countryCrossing: string;
    customBaggage: api.CustomBaggage = {
        bookable: false,
        options: null
    };
    exchangeRules: api.FlightModifyRules;
    expanded: boolean;
    externalOffers: api.FlightSearchExternalOffer[];
    externalPartner: api.FlightSearchPartner;
    farePrice = 0;
    flightFlags: api.SegmentFlags[];
    flightKind: api.FlightKind;
    hasMaintenanceStop: boolean;
    legs: LegModel[];
    seatMaps: api.SeatMap[] | null;
    multiFRType: number;
    priority: number;
    refundRules: api.FlightModifyRules;
    stopDuration: number[] = [0, 0]; // Used by StopDurationFilter
    fee = 0;
    promo_coupon_name: string | null;
    taxes = 0;
    tfid: number | null;
    uniqueId: string;
    validatingAirline: api.Airline;
    url: string;
    hasBusSegment: boolean;
    additionalOfferKeys: string[];

    constructor(f?: api.Flight, d?: api.FlightDictionaries) {
        if (f && d) {
            this.legs = f.legs.map(leg => new LegModel(leg, d));
            this.seatMaps = f.seatMaps?.map((seatMap) => ({
                ...seatMap
            }));
            

            this.airlines = [];
            this.allFlightAirlinesCode = [];
            this.legs.forEach((leg) => {
                leg.segments.forEach(segment => {
                    if (utils.find(this.airlines,
                        (airline: api.Airline) => {
                            return airline.iataCode === segment.operatingAirline.iataCode;
                        })) {
                        this.airlines.push(segment.operatingAirline);
                    }
                    if (!this.allFlightAirlinesCode.includes(segment.operatingAirline.iataCode)) {
                        this.allFlightAirlinesCode.push(segment.operatingAirline.iataCode);
                    }
                    if (segment.transport === api.SegmentTransport.bus) {
                        this.hasBusSegment = true;
                    }
                });

                if (leg.maintenanceStopCount > 0) {
                    this.hasMaintenanceStop = true;
                }
            });

            this.customBaggage = f.customBaggage;
            this.adapter = f.adapter;
            this.area = f.area;
            this.bookingClass = f.bookingClass;
            this.flightFlags = f.flightFlags;
            this.flightKind = f.flightKind == 'RegularSpecial' ? api.FlightKind.charter : f.flightKind;
            this.billingPrice = f.billingPrice;
            this.billingPricePerMonth = f.billingPricePerMonth;
            this.basePrice = f.basePrice;
            this.bookingChecked = f.bookingChecked;
            this.bookingPossible = f.bookingPossible;
            this.brand = f.brand;
            this.currencyCode = f.currencyCode;
            this.exchangeRules = f.exchangeRules;
            this.expanded = false;
            this.externalOffers = f.externalOffers;
            this.externalPartner = f.externalPartner;
            this.farePrice = f.farePrice;
            this.taxes = f.taxes;
            this.fee = f.fee;
            this.multiFRType = f.multiFRType;
            this.priority = f.priority;
            // eslint-disable-next-line @typescript-eslint/camelcase
            this.promo_coupon_name = f.promo_coupon_name;
            this.refundRules = f.refundRules;
            this.tfid = f.tfid;
            this.uniqueId = f.uniqueId;
            this.validatingAirline = d.airlines[f.validatingCarrierUID];
            this.stopDuration = this.calculateStopDuration();

            // Disable baggage if missing baggage options
            if (this.customBaggage
                && this.customBaggage.bookable
                && this.customBaggage.options
                && this.customBaggage.options.length === 0) {
                this.customBaggage.bookable = false;
            }

            this.additionalOfferKeys = f?.additionalOfferKeys ?? [];
        }
    }

    public get hasNoBaggage(): boolean {
        return this.legs[0].segments[0].baggage
            && this.legs[0].segments[0].baggage.pieceWeight == 0
            && this.legs[0].segments[0].baggage.pieces == 0;
    }

    public get destinations(): api.Destination[] {
        const allDestinations = this.legs
            .map(leg => leg.segments)
            .reduce((memo, value) => memo.concat(value), [])
            .map(segment => [segment.departureDestination, segment.arrivalDestination])
            .reduce((memo, value) => memo.concat(value), []);
        const result: api.Destination[] = [];
        const map = new Map();

        for (const destination of allDestinations) {
            if (!map.has(destination.uniqueId)) {
                map.set(destination.uniqueId, true);
                result.push(destination);
            }
        }

        return result;
    }

    public get airlinesCode(): string {
        let result = '';

        if (this.legs.length > 0) {
            result += this.legs[0].segments[0].airline.iataCode;

            if (this.legs.length > 1
                && this.legs[0].segments[0].airline.iataCode !== this.legs[1].segments[0].airline.iataCode) {
                result += '_' + this.legs[1].segments[0].airline.iataCode;
            }
        }

        return result;
    }

    public get is2OWorIncut(): boolean {
        return !!this.legs.filter(leg => leg.is2OWorIncut).length;
    }

    public get transportTypes() {
        const flightsTransportTypes: api.SegmentTransport[] = [];

        this.legs.forEach((leg) => {
            leg.segments.forEach((segment) => {
                if (!flightsTransportTypes.includes(segment.transport)) {
                    flightsTransportTypes.push(segment.transport);
                }
            });
        });

        return flightsTransportTypes;
    }

    public get transportIconNames() {
        const flightTransportIconNames: string[] = [];

        this.legs.forEach((leg) => {
            leg.segments.forEach((segment) => {
                if (!flightTransportIconNames.includes(segment.transportIconName)) {
                    flightTransportIconNames.push(segment.transportIconName);
                }
            });
        });

        return flightTransportIconNames;
    }

    private calculateStopDuration(): number[] {
        let minStopLength = 0;
        let maxStopLength = 0;

        this.legs.forEach(l => {
            for (let i = 0; i < l.segments.length - 1; i++) {
                const stopBeginTime = l.segments[i].arrivalDate;
                const stopEndTime = l.segments[i + 1].departureDate;
                const d = stopEndTime.diff(stopBeginTime, 'minute');

                if (!minStopLength || minStopLength > d) {
                    minStopLength = d;
                }

                if (!maxStopLength || maxStopLength < d) {
                    maxStopLength = d;
                }
            }
        });

        return [minStopLength, maxStopLength];
    }

    public getDestinationByUniqueId(id: string | number): api.Destination | null {
        return this.destinations.find(destination => destination.uniqueId == id) || null;
    }

    public getAvailableCustomBaggageItems(): api.CustomBaggageItem[] {
        if (this.customBaggage && this.customBaggage.options) {
            return this.customBaggage.options
                .reduce((memo: api.CustomBaggageOption[], value) => memo.concat(value), [])
                .map((option: api.CustomBaggageOption) => option.baggageOptions)
                .reduce((memo: api.CustomBaggageItem[], value) => memo.concat(value), []);
        }

        return [];
    }

    public getAvailableCustomBaggageItemsByFlightDirection(flightDirection: api.CustomBaggageFlightDirection): api.CustomBaggageItem[] {
        if (this.customBaggage && this.customBaggage.options) {
            return this.customBaggage.options
                .filter(option => option.customBaggageFlightDirection == flightDirection)
                .reduce((memo: api.CustomBaggageOption[], value) => memo.concat(value), [])
                .map((option: api.CustomBaggageOption) => option.baggageOptions)
                .reduce((memo: api.CustomBaggageItem[], value) => memo.concat(value), []);
        }

        return [];
    }

    public getAvailableCustomBaggageItemsByDestinationUIDs(destinationFromUID: string, destinationToUID: string): api.CustomBaggageItem[] {
        if (this.customBaggage && this.customBaggage.options) {
            return this.customBaggage.options
                .filter(option => option.destinationFromUID == destinationFromUID
                    && option.destinationToUID == destinationToUID)
                .reduce((memo: api.CustomBaggageOption[], value) => memo.concat(value), [])
                .map((option: api.CustomBaggageOption) => option.baggageOptions)
                .reduce((memo: api.CustomBaggageItem[], value) => memo.concat(value), []);
        }

        return [];
    }

    public isAirline(iataCode: string): boolean {
        let is = true;

        if (this.legs) {
            this.legs.forEach(l => {
                l.segments.forEach(s => {
                    if (s.airline.iataCode != iataCode
                        || s.operatingAirline.iataCode != iataCode) {
                        is = false;
                    }
                });
            });
        }

        return is;
    }

    /**
     * Updates flight with new details after check booking possibility.
     */
    public update(flight: api.Flight) {
        this.bookingChecked = flight.bookingChecked;
        this.bookingPossible = flight.bookingPossible;
        this.adapterData = flight.adapterData;
        this.customBaggage = flight.customBaggage;
        this.exchangeRules = flight.exchangeRules;
        this.refundRules = flight.refundRules;

        if(!this.seatMaps && flight.seatMaps){
            this.seatMaps = flight.seatMaps;
        }

        flight.legs.forEach((leg, legIndex) => {
            leg.segments.forEach((segment, segmentIndex) => {
                this.legs[legIndex].segments[segmentIndex].baggage = segment.baggage;
                this.legs[legIndex].segments[segmentIndex].baggageCabin = segment.baggageCabin;
            });
        });
    }

    get detailsLabel() {
        return this.hasBusSegment ? i18next.t('button_route_details') : i18next.t('button_flight_details');
    }
}

export class FlightGroupModel {
    public brandGroupGuid: string;
    public selectedFlightId: string;
    public defaultFlightId: string;
    public flights: FlightModel[];
    public brandChangeState: string;

    get selectedFlight() {
        if (this.selectedFlightId) {
            return this.flights.find(flight => flight.uniqueId === this.selectedFlightId) || null;
        }
        return null;
    }
}
